import {
  type SimilarSuggestion,
  SUGGESTION_SCALE,
  SUGGESTION_STATUSES,
  type SuggestionDetail,
  type SuggestionStatus,
  type SuggestionSummary,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Award, GitMerge, ThumbsUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { HistoryButton } from '../../components/History';
import { useCrumbs } from '../../layout/crumbs';
import { ApiError, api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { SUGGESTION_TONE } from './SupportPage';
import { TicketAttachments } from './TicketPage';

/** Предложение: голоса, статус с причиной, обсуждение; автор видит смену статуса (БЗ-28…БЗ-30) */
export function SuggestionPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  // Переход по ссылке на другое предложение не переносит набранное: комментарий, статус, оценку, выбор основного
  return <SuggestionCard key={id} id={id} />;
}

function SuggestionCard({ id }: { id: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['support', 'suggestion', id],
    queryFn: () => api<SuggestionDetail>(`/support/suggestions/${id}`),
  });
  const s = q.data;
  useCrumbs(s ? [{ label: s.number, title: s.title }] : null, q.error);
  const [comment, setComment] = useState('');
  const [status, setStatus] = useState<SuggestionStatus | ''>('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ['support'] });
  useEffect(() => {
    if (s?.unread)
      api(`/support/suggestions/${id}/seen`, { method: 'POST' })
        .then(() => qc.invalidateQueries({ queryKey: ['support'] }))
        .catch(() => {});
  }, [s?.unread, id, qc]);
  const vote = useMutation({
    mutationFn: () =>
      api<SuggestionDetail>(`/support/suggestions/${id}/vote`, { method: s?.voted ? 'DELETE' : 'POST' }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не удалось проголосовать', 'danger'),
  });
  const send = useMutation({
    mutationFn: () =>
      api<SuggestionDetail>(`/support/suggestions/${id}/comments`, {
        method: 'POST',
        body: { body: comment },
      }),
    onSuccess: () => {
      setComment('');
      refresh();
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : 'Не удалось отправить', 'danger'),
  });
  const change = useMutation({
    mutationFn: () =>
      api<SuggestionDetail>(`/support/suggestions/${id}/status`, {
        method: 'POST',
        body: { status, reason: reason.trim() || null },
      }),
    onSuccess: () => {
      setStatus('');
      setReason('');
      setErr(null);
      toast('Статус изменён, автор увидит это у себя');
      refresh();
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? (e.fieldErrors().reason ?? e.message) : 'Не удалось изменить статус'),
  });

  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError || !s)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  return (
    <Page>
      <PageHeader
        title={`${s.number}. ${s.title}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={SUGGESTION_TONE[s.status]}>{SUGGESTION_STATUSES[s.status]}</Badge>
            <span>
              {s.authorName} · {dateTime(s.createdAt)}
            </span>
            {/* Премия за предложение (БЗ-35): номинация и период, без суммы */}
            {s.awards.map((a) => (
              <Badge key={`${a.nomination}|${a.periodLabel}`} tone="accent">
                <Award className="size-3.5" aria-hidden />
                {a.nomination} · {a.periodLabel}
              </Badge>
            ))}
          </span>
        }
        actions={
          <>
            <Button
              variant={s.voted ? 'primary' : 'secondary'}
              icon={<ThumbsUp className="size-4" />}
              loading={vote.isPending}
              aria-pressed={s.voted}
              onClick={() => vote.mutate()}
            >
              {s.voted ? 'Вы проголосовали' : 'Проголосовать'}: {s.votes}
            </Button>
            {s.canManage ? <HistoryButton table="suggestions" rowId={s.id} title={s.number} /> : null}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          {s.duplicateOf ? (
            <div className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-[14px]">
              Такое предложение уже было:{' '}
              <Link
                to="/support/suggestions/$id"
                params={{ id: s.duplicateOf.id }}
                className="inline-flex min-h-11 items-center font-medium [overflow-wrap:anywhere] underline underline-offset-2 md:min-h-0"
              >
                {s.duplicateOf.number} · {s.duplicateOf.title}
              </Link>
              <span className="block">Голоса перенесены к нему — следите за ним.</span>
            </div>
          ) : null}
          {/* Из панели «Сообщить» доработка приходит одним текстом — «Что неудобно» тогда пустое */}
          {s.problem ? (
            <Panel title="Что неудобно">
              <p className="text-[14px] leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap">
                {s.problem}
              </p>
            </Panel>
          ) : null}
          <Panel title="Что предлагается">
            <p className="text-[14px] leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap">{s.proposal}</p>
          </Panel>
          {s.statusReason ? (
            <Panel title={s.status === 'rejected' ? 'Почему отклонено' : 'Пояснение к статусу'}>
              <p className="text-[14px] leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap">
                {s.statusReason}
              </p>
            </Panel>
          ) : null}
          <Panel title="Обсуждение и история статуса">
            <ul className="flex flex-col gap-3">
              {s.events.length === 0 ? (
                <li className="text-[14px] text-ink-2">Пока никто не писал.</li>
              ) : null}
              {s.events.map((e) =>
                e.kind === 'status' ? (
                  <li key={e.id} className="text-[13px] text-ink-2">
                    {dateTime(e.createdAt)} · статус:{' '}
                    {e.fromStatus ? `${SUGGESTION_STATUSES[e.fromStatus]} → ` : ''}
                    {e.toStatus ? SUGGESTION_STATUSES[e.toStatus] : ''}
                    {e.authorName ? ` (${e.authorName})` : ''}
                    {e.body ? `. ${e.body}` : ''}
                  </li>
                ) : (
                  <li key={e.id} className="rounded-md border border-line p-3">
                    <div className="mb-1 text-[12px] text-ink-2">
                      {e.authorName ?? 'Сотрудник'} · {dateTime(e.createdAt)}
                    </div>
                    <p className="text-[14px] leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap">
                      {e.body}
                    </p>
                  </li>
                ),
              )}
            </ul>
            <div className="mt-4 flex flex-col gap-3">
              <Field label="Комментарий" htmlFor="sg-comment">
                <Textarea
                  id="sg-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={2000}
                  rows={3}
                />
              </Field>
              <div>
                <Button loading={send.isPending} disabled={!comment.trim()} onClick={() => send.mutate()}>
                  Отправить
                </Button>
              </div>
            </div>
          </Panel>
        </div>
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Сведения">
            <KeyValue
              items={[
                { label: 'Статус', value: SUGGESTION_STATUSES[s.status] },
                { label: 'Голосов', value: String(s.votes) },
                { label: 'Статус изменён', value: dateTime(s.statusChangedAt) },
                { label: 'Страница', value: s.pagePath ?? '—' },
                ...(s.releasedAt ? [{ label: 'Выпущено', value: dateTime(s.releasedAt) }] : []),
                ...(s.duplicates.length
                  ? [
                      {
                        label: 'Присоединены повторы',
                        value: (
                          <span className="flex flex-wrap gap-x-2">
                            {s.duplicates.map((d) => (
                              <Link
                                key={d.id}
                                to="/support/suggestions/$id"
                                params={{ id: d.id }}
                                className="inline-flex min-h-11 items-center underline underline-offset-2 md:min-h-0"
                              >
                                {d.number}
                              </Link>
                            ))}
                          </span>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Panel>
          {/* Оценка в ключе: сохранённая в другом месте (список «что делать первым») не затирается старой формой */}
          {s.canManage ? <AssessmentPanel key={s.assessment?.at ?? 'none'} s={s} /> : null}
          {s.canManage && s.duplicateOf ? (
            <Panel title="Статус">
              <p className="text-[14px] leading-6">
                Это повтор {s.duplicateOf.number}: статус ставится основному предложению — голоса этого
                предложения уже у него.
              </p>
            </Panel>
          ) : s.canManage ? (
            <Panel title="Изменить статус">
              <div className="flex flex-col gap-3">
                <Field label="Новый статус" htmlFor="sg-status">
                  <Select
                    className="h-11 md:h-10"
                    id="sg-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as SuggestionStatus | '')}
                  >
                    <option value="">Выберите</option>
                    {Object.entries(SUGGESTION_STATUSES).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field
                  label={status === 'rejected' ? 'Почему отклонено (обязательно)' : 'Пояснение автору'}
                  htmlFor="sg-reason"
                  hint={status === 'released' ? 'Это пояснение появится в «Что нового»' : undefined}
                >
                  <Input
                    className="h-11 md:h-10"
                    id="sg-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={1000}
                  />
                </Field>
                {err ? <ErrorBox error={new Error(err)} /> : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="primary"
                    disabled={!status}
                    loading={change.isPending}
                    onClick={() => change.mutate()}
                  >
                    Сохранить статус
                  </Button>
                  {s.status !== 'released' ? <DuplicateButton s={s} /> : null}
                </div>
              </div>
            </Panel>
          ) : null}
          <TicketAttachments entityType="support_suggestion" entityId={s.id} canAdd={s.canManage} />
        </div>
      </div>
    </Page>
  );
}

const SCORES = [1, 2, 3, 4, 5] as const;

/** Оценка пользы и трудоёмкости (БЗ-33): видят и ставят специалисты поддержки, изменения — в истории */
function AssessmentPanel({ s }: { s: SuggestionDetail }) {
  const qc = useQueryClient();
  const [benefit, setBenefit] = useState<number | ''>(s.assessment?.benefit ?? '');
  const [effort, setEffort] = useState<number | ''>(s.assessment?.effort ?? '');
  const [note, setNote] = useState(s.assessment?.note ?? '');
  const save = useMutation({
    mutationFn: () =>
      api<SuggestionDetail>(`/support/suggestions/${s.id}/assessment`, {
        method: 'PUT',
        body: { benefit, effort, note: note.trim() || null },
      }),
    onSuccess: () => {
      toast('Оценка сохранена');
      void qc.invalidateQueries({ queryKey: ['support'] });
    },
  });
  return (
    <Panel title="Оценка" meta={s.priority !== null ? `приоритет ${s.priority}` : undefined}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Польза" htmlFor="sg-benefit">
            <Select
              id="sg-benefit"
              className="h-11 md:h-10"
              value={benefit}
              onChange={(e) => setBenefit(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Не оценена</option>
              {SCORES.map((n) => (
                <option key={n} value={n}>
                  {n} — {SUGGESTION_SCALE[n]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Трудоёмкость" htmlFor="sg-effort">
            <Select
              id="sg-effort"
              className="h-11 md:h-10"
              value={effort}
              onChange={(e) => setEffort(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Не оценена</option>
              {SCORES.map((n) => (
                <option key={n} value={n}>
                  {n} — {SUGGESTION_SCALE[n]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field
          label="Комментарий к оценке"
          htmlFor="sg-assess-note"
          hint="Видят только специалисты поддержки"
        >
          <Input
            id="sg-assess-note"
            className="h-11 md:h-10"
            value={note}
            maxLength={1000}
            onChange={(e) => setNote(e.target.value)}
          />
        </Field>
        <p className="text-[12px] text-ink-2">
          Приоритет = 2 × польза − трудоёмкость + голоса (не больше 10).
          {s.assessment
            ? ` Оценил ${s.assessment.byName ?? 'специалист'}, ${dateTime(s.assessment.at)}.`
            : ''}
        </p>
        {save.isError ? <ErrorBox error={save.error} /> : null}
        <div>
          <Button
            loading={save.isPending}
            disabled={benefit === '' || effort === ''}
            onClick={() => save.mutate()}
          >
            Сохранить оценку
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/** Номер из поля: «ПРД-0012», «прд 12», «12» → 12 */
const numberOf = (text: string): number | null => {
  const m = text.match(/(\d{1,7})\s*$/);
  return m ? Number(m[1]) : null;
};

/**
 * Это повтор: основное — из похожих по названию или по номеру предложения (любого, а не только недавнего);
 * предложение отклонится со ссылкой, голоса перейдут к основному (БЗ-32)
 */
function DuplicateButton({ s }: { s: SuggestionDetail }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [main, setMain] = useState('');
  const [number, setNumber] = useState('');
  // Похожие ищутся по словам названия: короче трёх знаков искать нечего
  const words = s.title.trim().length >= 3;
  const similar = useQuery({
    queryKey: ['support', 'duplicate-of', s.id],
    queryFn: () => api<SimilarSuggestion[]>('/support/suggestions/similar', { query: { q: s.title } }),
    enabled: open && words,
  });
  const seq = numberOf(number);
  const byNumber = useQuery({
    queryKey: ['support', 'suggestions', 'by-number', seq],
    queryFn: () => api<SuggestionSummary[]>('/support/suggestions', { query: { seq: seq ?? undefined } }),
    enabled: open && seq !== null,
  });
  const options = (similar.data ?? []).filter((x) => x.id !== s.id);
  const found = (byNumber.data ?? [])[0];
  const run = useMutation({
    mutationFn: () =>
      api<SuggestionDetail>(`/support/suggestions/${s.id}/duplicate`, { method: 'POST', body: { of: main } }),
    onSuccess: () => {
      toast('Объединено: голоса перенесены, автор получит уведомление');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['support'] });
      void qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
  const close = (o: boolean) => {
    setOpen(o);
    if (!o) {
      setMain('');
      setNumber('');
      run.reset();
    }
  };
  const choice = (x: {
    id: string;
    number: string;
    title: string;
    status: SuggestionStatus;
    votes: number;
  }) => (
    <label
      key={x.id}
      className="flex min-h-11 cursor-pointer items-start gap-2.5 rounded-md border border-line px-3 py-2 has-[:checked]:border-ink"
    >
      <input
        type="radio"
        name="dup-main"
        value={x.id}
        checked={main === x.id}
        onChange={() => setMain(x.id)}
        className="mt-1"
      />
      <span className="min-w-0 text-[14px] [overflow-wrap:anywhere]">
        {x.number} · {x.title}
        <span className="block text-[12px] text-ink-2">
          {SUGGESTION_STATUSES[x.status]} · голосов {x.votes}
        </span>
      </span>
    </label>
  );
  return (
    <>
      <Button icon={<GitMerge className="size-4" aria-hidden />} onClick={() => setOpen(true)}>
        Это повтор
      </Button>
      <Dialog
        open={open}
        onOpenChange={close}
        title={`${s.number} — повтор`}
        description="Предложение отклонится со ссылкой на основное, голоса и голос автора перейдут к основному"
        footer={
          <>
            <Button variant="ghost" onClick={() => close(false)}>
              Отмена
            </Button>
            <Button variant="primary" disabled={!main} loading={run.isPending} onClick={() => run.mutate()}>
              Объединить
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {words && similar.isPending ? (
            <Spinner />
          ) : similar.isError ? (
            <ErrorBox error={similar.error} />
          ) : options.length === 0 ? (
            <p className="text-[14px] text-ink-2">Похожих по названию предложений не нашлось.</p>
          ) : (
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-[13px] font-medium">Похожие — основное предложение</legend>
              {options.map(choice)}
            </fieldset>
          )}
          <Field
            label="Или основное по номеру"
            htmlFor="dup-number"
            hint={
              seq === null
                ? 'Например, ПРД-0012 или 12'
                : byNumber.isPending
                  ? 'Ищу…'
                  : found
                    ? undefined
                    : byNumber.isError
                      ? undefined
                      : `Предложения ПРД-${String(seq).padStart(4, '0')} нет`
            }
            error={byNumber.isError ? 'Не удалось найти предложение по номеру' : undefined}
          >
            <Input
              id="dup-number"
              className="h-11 md:h-10"
              value={number}
              maxLength={20}
              onChange={(e) => setNumber(e.target.value)}
            />
          </Field>
          {found && found.id !== s.id && !options.some((o) => o.id === found.id) ? choice(found) : null}
          {found && found.id === s.id ? (
            <p className="text-[13px] text-ink-2">Это то же предложение — выберите другое.</p>
          ) : null}
          {run.isError ? <ErrorBox error={run.error} /> : null}
        </div>
      </Dialog>
    </>
  );
}
